import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import Dropdown from '@prisjakt/ui-dropdown';
import { AppContext } from '@prisjakt/ui-app-context';

// Subtract years, months or days from a date (e.g. 3m or 1y).
export const subtractFromDate = (period, date = new Date()) => {
  const duration = +period.slice(0, -1);

  switch (period[period.length - 1]) {
    case 'y':
      date.setFullYear(date.getFullYear() - duration);
      break;

    case 'm':
      date.setMonth(date.getMonth() - duration);
      break;

    case 'd':
      date.setDate(date.getDate() - duration);
      break;

    default:
    // No default
  }

  return date;
};

const pad = number => (number < 10 ? `0${number}` : number);

// Convert date to string (YYYY-MM-DD).
export const toDateString = date =>
  `${pad(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;

const IntervalSelector = props => {
  const { t } = useContext(AppContext);
  const { min, max, onChange, selected } = props;
  const minDate = new Date(min);

  const allOptions = [
    { name: `3 ${t('common', 'time.month', { n: 3 })}`, period: '3m' },
    { name: `6 ${t('common', 'time.month', { n: 6 })}`, period: '6m' },
    { name: `1 ${t('common', 'time.year', { n: 1 })}`, period: '1y' },
    { name: `2 ${t('common', 'time.year', { n: 2 })}`, period: '2y' },
    { name: t('product', 'price-history.all-dates'), period: 'all' },
  ];

  // Remove unselectable options for products newer than 2 years.
  const options = allOptions.filter(
    ({ period }) => period === 'all' || subtractFromDate(period) >= minDate
  );

  // Don't show the dropdown if the only value is 'all'.
  if (options.length < 2) {
    return null;
  }

  let selectedOptionName;
  const selectedOption = options.filter(o => o.period === selected);
  if (selectedOption[0]) {
    selectedOptionName = selectedOption[0].name;
  }

  // Default value should be "all" on products newer than 2 years, and the
  // highest value (2y) on products older than 2 years.
  const defaultIndex =
    allOptions.length === options.length
      ? options.length - 2
      : options.length - 1;
  const defaultSelected = options[defaultIndex].name;

  return (
    <Dropdown
      title={t('common', 'button.show')}
      type="select-box-type"
      selected={selectedOptionName || defaultSelected}
      onSelect={option => {
        if (option.period === 'all') {
          onChange({ ...option, period: option.period, min });
        } else {
          onChange({
            ...option,
            period: option.period,
            min: toDateString(subtractFromDate(option.period, new Date(max))),
          });
        }
      }}
      options={options}
    />
  );
};

IntervalSelector.propTypes = {
  min: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  selected: PropTypes.string,
};

IntervalSelector.defaultProps = {
  selected: undefined,
};

export default memo(IntervalSelector);
